import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useJoinCourse } from "@/lib/useJoinCourse";
import YogaCourses from "@/components/yoga/YogaCourses";
import OneOnOneBooking from "@/components/yoga/OneOnOneBooking";
import NextLiveClass from "@/components/yoga/NextLiveClass";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Check, Lock, Play, Video, Clock, Calendar, Sparkles } from "lucide-react";

function pct(done, total) { return total ? Math.round((done / total) * 100) : 0; }

function fmtCountdown(ms) {
  if (ms <= 0) return "Live now";
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${sec}s`;
  return `${m}m ${sec}s`;
}

export default function YogaDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [nextDay, setNextDay] = useState(0);
  const [hasSub, setHasSub] = useState(false);
  const [sub, setSub] = useState(null);
  const [live, setLive] = useState({ upcoming: [], past: [] });
  const [now, setNow] = useState(Date.now());
  const [course, setCourse] = useState("");
  const { join, joining } = useJoinCourse();

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/yoga/dashboard"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const load = async () => {
    try {
      const [l, s, live] = await Promise.all([
        api.get("/yoga/lessons"),
        api.get("/yoga/subscription"),
        api.get("/yoga/live-sessions"),
      ]);
      setLessons(l.data.lessons);
      setNextDay(l.data.next_day || (l.data.has_subscription ? 1 : 0));
      setHasSub(l.data.has_subscription);
      setSub(s.data.subscription);
      setCourse(s.data.subscription?.course || "");
      setLive(live.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    }
  };

  const doneCount = useMemo(() => lessons.filter((l) => l.completed).length, [lessons]);
  const todayLesson = useMemo(() => lessons.find((l) => l.day === nextDay), [lessons, nextDay]);
  const nextLive = live.upcoming?.[0];
  const nextLiveMs = nextLive ? new Date(nextLive.scheduled_at).getTime() - now : 0;

  const chooseCourse = async (slug) => {
    try {
      await api.post("/yoga/course", { slug });
      setCourse(slug);
      toast.success("Course saved — your guru will follow this path with you");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the course");
    }
  };

  if (loading || !user) return <main className="p-12 text-center font-display text-2xl">Loading…</main>;

  // Not subscribed → show CTA
  if (!hasSub) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-secondary/15 mx-auto flex items-center justify-center mb-6">
          <Sparkles className="text-secondary" size={28}/>
        </div>
        <h1 className="font-display text-4xl lg:text-5xl mb-3">Namaste, {user.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Your 21-day journey is one step away. ₹100 unlocks all lessons, the live classes and every course —
          it then continues at ₹251/month, cancel anytime.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button onClick={join} disabled={joining} className="btn-pill bg-primary text-primary-foreground h-12 px-8" data-testid="yoga-start-checkout">
            {joining ? "Opening payment…" : "Join Course — ₹100"}
          </Button>
          <Link to="/yoga/day/1">
            <Button variant="outline" className="btn-pill h-12 px-8 border-secondary/50 text-secondary" data-testid="yoga-try-day-1">
              <Play size={14} className="mr-1.5"/> Try Day 1 free
            </Button>
          </Link>
          <Link to="/yoga"><Button variant="ghost" className="btn-pill h-12 px-8" data-testid="yoga-learn-more">Learn more</Button></Link>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Your Journey</div>
          <h1 className="font-display text-4xl lg:text-5xl">Welcome back, {user.name?.split(" ")[0]}.</h1>
          <p className="text-muted-foreground mt-1">
            Membership active until <b>{sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString() : "—"}</b>
          </p>
        </div>
        <Link to="/yoga/billing"><Button variant="outline" className="btn-pill" data-testid="yoga-manage-btn">Manage Membership</Button></Link>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {/* Today's practice */}
        <Card className="lg:col-span-2 border-border overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-6 lg:p-8">
              <div className="overline mb-2">Today's Practice</div>
              <div className="flex items-baseline gap-2 mb-2">
                <div className="font-display text-4xl">Day {todayLesson?.day || 1}</div>
                <span className="text-sm text-muted-foreground">of 21</span>
              </div>
              <h2 className="font-display text-2xl mb-2">{todayLesson?.title || "Ready to begin"}</h2>
              <p className="text-sm text-muted-foreground mb-4">{todayLesson?.description}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mb-6">
                <span className="flex items-center gap-1"><Clock size={12}/>{todayLesson?.duration_minutes || 15} min</span>
                <span className="flex items-center gap-1"><Video size={12}/>{todayLesson?.level || "Beginner"}</span>
              </div>
              {todayLesson && (
                <Link to={`/yoga/day/${todayLesson.day}`}>
                  <Button className="btn-pill bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6" data-testid="yoga-today-play">
                    <Play size={14} className="mr-2 fill-current"/> Start today's session
                  </Button>
                </Link>
              )}
            </div>
            <div className="hidden md:block bg-gradient-to-br from-primary/10 to-secondary/10">
              <img src="https://images.unsplash.com/photo-1552693673-1bf958298935?auto=format&fit=crop&w=800&q=80" alt="" className="w-full h-full object-cover"/>
            </div>
          </div>
        </Card>

        {/* Progress */}
        <Card className="border-border">
          <CardContent className="p-6 lg:p-8">
            <div className="overline mb-2">Progress</div>
            <div className="font-display text-5xl mb-1">{doneCount}<span className="text-2xl text-muted-foreground">/21</span></div>
            <div className="text-sm text-muted-foreground mb-4">days completed</div>
            <div className="h-2 bg-accent rounded-full overflow-hidden mb-6">
              <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct(doneCount, 21)}%` }} />
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {lessons.map((l) => (
                <div key={l.day} title={`Day ${l.day} — ${l.title}`}
                  className={`aspect-square rounded-md text-[9px] font-medium flex items-center justify-center
                    ${l.completed ? "bg-primary text-primary-foreground" : l.unlocked ? "bg-secondary/20 text-secondary" : "bg-muted text-muted-foreground/60"}`}>
                  {l.day}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live Session */}
      {nextLive && (
        <Card className="border-border mb-10 overflow-hidden">
          <CardContent className="p-6 lg:p-8 grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="overline mb-2">Next Live Session</div>
              <h3 className="font-display text-3xl mb-2">{nextLive.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">{nextLive.description}</p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar size={12}/>{new Date(nextLive.scheduled_at).toLocaleString([], { weekday:"short", month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" })}</span>
                <span className="flex items-center gap-1"><Clock size={12}/>{nextLive.duration_minutes} min</span>
              </div>
            </div>
            <div className="text-center">
              <div className="overline mb-3">Starts in</div>
              <div className="font-display text-5xl lg:text-6xl text-primary tabular-nums" data-testid="live-countdown">
                {fmtCountdown(nextLiveMs)}
              </div>
              <a href={nextLive.join_url} target="_blank" rel="noreferrer">
                <Button variant="outline" className="btn-pill border-primary/30 mt-6" data-testid="live-join-btn">
                  Join Live Class
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Path */}
      <div className="mb-6 flex items-end justify-between">
        <h2 className="font-display text-3xl">The 21-Day Path</h2>
        <span className="text-sm text-muted-foreground">Complete each day to unlock the next.</span>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {lessons.map((l) => (
          <Card key={l.day} className={`border-border transition-all ${l.unlocked ? "hover:border-primary/40" : "opacity-70"}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Day {l.day}</div>
                {l.completed ? (
                  <Badge className="bg-primary text-primary-foreground text-[10px]"><Check size={10} className="mr-0.5"/>Done</Badge>
                ) : l.unlocked ? (
                  <Badge variant="outline" className="border-secondary/40 text-secondary text-[10px]">Unlocked</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]"><Lock size={10} className="mr-0.5"/>Locked</Badge>
                )}
              </div>
              <h4 className="font-display text-xl mb-2">{l.title}</h4>
              <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{l.description}</p>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Clock size={11}/>{l.duration_minutes} min</span>
                {l.unlocked ? (
                  <Link to={`/yoga/day/${l.day}`} className="text-primary underline" data-testid={`yoga-day-${l.day}`}>
                    {l.completed ? "Watch again →" : "Start →"}
                  </Link>
                ) : (
                  <span>Locked</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-12 space-y-5">
        <NextLiveClass />
        <OneOnOneBooking />
      </div>

      <div className="-mx-6 lg:-mx-12 mt-6">
        <YogaCourses onSelect={chooseCourse} selected={course} />
      </div>
    </main>
  );
}
